/**
 * Throwaway names, for playing without giving your own.
 *
 * There are no accounts here, so the name you type is the only thing anyone at
 * the table learns about you — and an invite code can end up in a group chat
 * with people you do not know. Picking a pub regular to hide behind should be
 * one tap, not a thing you have to think up on the spot.
 *
 * Two lists rather than one flat set of names: 24 x 26 is 624 combinations from
 * 50 words, so a table of eight rarely collides, and the server appends a
 * numeric suffix if it ever does.
 */

const ADJECTIVES = [
  'Idle', 'Lucky', 'Quiet', 'Crooked', 'Merry', 'Sly', 'Bold', 'Weary',
  'Nimble', 'Grumpy', 'Cheery', 'Rusty', 'Silver', 'Brazen', 'Humble', 'Restless',
  'Dapper', 'Shifty', 'Steady', 'Wistful', 'Canny', 'Jolly', 'Solemn', 'Feckless',
] as const;

const CREATURES = [
  'Magpie', 'Ferret', 'Badger', 'Sparrow', 'Otter', 'Heron', 'Stoat', 'Raven',
  'Hare', 'Wren', 'Fox', 'Mole', 'Adder', 'Finch', 'Marten', 'Weasel',
  'Kestrel', 'Pheasant', 'Terrier', 'Lurcher', 'Whippet', 'Jackdaw', 'Rook', 'Crow',
  'Owl', 'Bittern',
] as const;

/** Longest name the server will take, so nothing generated here gets truncated. */
const MAX_LENGTH = 16;

const pick = <T,>(list: readonly T[]): T => list[Math.floor(Math.random() * list.length)] as T;

/**
 * A random two-word alias, never the one passed in — pressing the button and
 * getting the same name back reads as a broken button rather than chance.
 */
export function randomAlias(avoid?: string): string {
  for (let attempt = 0; attempt < 12; attempt++) {
    const candidate = `${pick(ADJECTIVES)} ${pick(CREATURES)}`;
    if (candidate.length <= MAX_LENGTH && candidate !== avoid) return candidate;
  }
  // Every combination fits comfortably, so this is unreachable in practice;
  // returning something valid beats looping.
  return `Quiet ${pick(CREATURES)}`.slice(0, MAX_LENGTH);
}
